import { useCallback, useEffect, useRef, useState } from 'react';

import { useVisualization } from '@/context/visualization-context';
import {
    ViewportSnapshotService,
    type AnnotationData,
    type SnapshotBaseVariant,
    type ViewportSnapshot,
} from '@/visualization';

interface ViewportSnapshotState {
    /** All persisted snapshots. */
    snapshots: ViewportSnapshot[];
    /** Draft snapshot awaiting user save. Shared across all hook instances via service events. */
    draftSnapshot: ViewportSnapshot | null;

    /** Capture images as a draft (not persisted). Opens preview in all consumers. */
    captureDraft: (viewportId: string) => Promise<ViewportSnapshot | null>;
    /** Persist the current draft, optionally with overrides (e.g. chosen base variant). */
    saveDraft: (overrides?: Partial<ViewportSnapshot>) => void;
    /** Discard the draft without saving. */
    dismissDraft: () => void;

    removeSnapshot: (id: string) => void;
    toggleReportInclusion: (id: string) => void;
    updateSnapshotAnnotations: (
        id: string,
        annotations: AnnotationData[],
        annotatedDataUrl: string | null,
        baseVariant: SnapshotBaseVariant
    ) => void;
    updateSnapshotDescription: (id: string, description: string) => void;
    clearAll: () => void;
}

function buildSnapshots(service: ViewportSnapshotService): ViewportSnapshot[] {
    return service.getAllSnapshots();
}

export function useViewportSnapshot(): ViewportSnapshotState {
    const { getServiceManager } = useVisualization();
    const [snapshots, setSnapshots] = useState<ViewportSnapshot[]>([]);
    const [draftSnapshot, setDraftSnapshot] = useState<ViewportSnapshot | null>(null);
    const serviceRef = useRef<ViewportSnapshotService | null>(null);

    useEffect(() => {
        const service = getServiceManager().getService<ViewportSnapshotService>(
            ViewportSnapshotService.SERVICE_NAME
        );
        serviceRef.current = service;

        setSnapshots(buildSnapshots(service));
        setDraftSnapshot(service.getPendingDraft());

        const unsubStored = service.subscribe(service.EVENTS.SNAPSHOT_STORED, () => {
            setSnapshots(buildSnapshots(service));
        });
        const unsubRemoved = service.subscribe(service.EVENTS.SNAPSHOT_REMOVED, () => {
            setSnapshots(buildSnapshots(service));
        });
        const unsubCleared = service.subscribe(service.EVENTS.SNAPSHOTS_CLEARED, () => {
            setSnapshots([]);
            setDraftSnapshot(null);
        });
        const unsubUpdated = service.subscribe(service.EVENTS.SNAPSHOT_UPDATED, () => {
            setSnapshots(buildSnapshots(service));
        });
        const unsubRestored = service.subscribe(service.EVENTS.SNAPSHOT_RESTORED, () => {
            setSnapshots(buildSnapshots(service));
        });
        const unsubDraftCaptured = service.subscribe(
            service.EVENTS.DRAFT_CAPTURED,
            (payload: { snapshot: ViewportSnapshot }) => {
                setDraftSnapshot(payload.snapshot);
            }
        );
        const unsubDraftDismissed = service.subscribe(service.EVENTS.DRAFT_DISMISSED, () => {
            setDraftSnapshot(null);
        });

        return () => {
            unsubStored();
            unsubRemoved();
            unsubCleared();
            unsubUpdated();
            unsubRestored();
            unsubDraftCaptured();
            unsubDraftDismissed();
        };
    }, [getServiceManager]);

    const captureDraft = useCallback(async (viewportId: string): Promise<ViewportSnapshot | null> => {
        const service = serviceRef.current;
        if (!service) return null;
        return service.captureDraft(viewportId);
    }, []);

    const saveDraft = useCallback((overrides?: Partial<ViewportSnapshot>) => {
        const service = serviceRef.current;
        if (!service) return;
        service.saveDraft(overrides);
    }, []);

    const dismissDraft = useCallback(() => {
        serviceRef.current?.dismissDraft();
    }, []);

    return {
        snapshots,
        draftSnapshot,
        captureDraft,
        saveDraft,
        dismissDraft,
        removeSnapshot: (id) => serviceRef.current?.removeSnapshot(id),
        toggleReportInclusion: (id) => serviceRef.current?.toggleReportInclusion(id),
        updateSnapshotAnnotations: (id, annotations, annotatedDataUrl, baseVariant) =>
            serviceRef.current?.updateSnapshotAnnotations(id, annotations, annotatedDataUrl, baseVariant),
        updateSnapshotDescription: (id, description) =>
            serviceRef.current?.updateSnapshotDescription(id, description),
        clearAll: () => serviceRef.current?.clearAll(),
    };
}
